import { useEffect, useMemo, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Search, RefreshCw, ChevronLeft, ChevronRight, Loader2, CheckCircle2, XCircle, Clock, Grid3x3, Ticket } from "lucide-react";

const PAGE_SIZE = 500;
const pad = (n) => String(n).padStart(4, "0");

export default function FichaMap({ onPick, onCellClick, refreshKey, publicEndpoint = false }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [query, setQuery] = useState("");
  const [result, setResult] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(publicEndpoint ? "/public/fichas/status" : "/fichas/status");
      setData(res.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, [publicEndpoint]);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  const { soldMap, reservedMap, max } = useMemo(() => {
    const sm = new Map();
    (data?.sold || []).forEach((s) => sm.set(s.number, s));
    const rm = new Map();
    (data?.reserved || []).forEach((r) => rm.set(r.number, r));
    return { soldMap: sm, reservedMap: rm, max: data?.max || 9999 };
  }, [data]);

  const totalCount = max + 1; // includes 0000
  const totalPages = Math.ceil(totalCount / PAGE_SIZE);
  const start = page * PAGE_SIZE;            // 0-based (0000..)
  const end = Math.min(max, start + PAGE_SIZE - 1);
  const soldCount = data?.sold_count || 0;
  const reservedCount = data?.reserved_count || 0;
  const available = totalCount - soldCount - reservedCount;

  const checkNumber = (e) => {
    e?.preventDefault?.();
    if (query === "") { setResult({ error: `Ingresa un número entre 0000 y ${max}` }); return; }
    const n = parseInt(query, 10);
    if (Number.isNaN(n) || n < 0 || n > max) {
      setResult({ error: `Ingresa un número entre 0000 y ${max}` });
      return;
    }
    const sold = soldMap.get(n);
    const reserved = reservedMap.get(n);
    if (sold) setResult({ number: n, state: "sold", ...sold });
    else if (reserved) setResult({ number: n, state: "reserved", ...reserved });
    else setResult({ number: n, state: "free" });
    setPage(Math.floor(n / PAGE_SIZE));
  };

  const cells = [];
  for (let n = start; n <= end; n++) {
    const sold = soldMap.get(n);
    const reserved = reservedMap.get(n);
    const cls = sold
      ? "bg-[#E63946]/25 text-[#E63946] border-[#E63946]/40 cursor-help"
      : reserved
      ? "bg-[#F0B91B]/25 text-[#F0B91B] border-[#F0B91B]/50 cursor-help"
      : "bg-[#7FD1AE]/10 text-[#7FD1AE]/80 border-[#7FD1AE]/25 hover:bg-[#7FD1AE]/25";
    cells.push(
      <button
        key={n}
        title={sold ? (sold.seller_name ? `Vendida por ${sold.seller_name}` : "Vendida") : reserved ? (reserved.seller_name ? `Reservada por ${reserved.seller_name}` : "Reservada") : "Disponible"}
        onClick={() => {
          if (onCellClick) {
            const state = sold ? "sold" : reserved ? "reserved" : "free";
            const info = sold || reserved || null;
            onCellClick({ number: n, ficha: pad(n), state, info });
            return;
          }
          if (sold) toast(`Ficha #${pad(n)} vendida`, { description: `Vendedor: ${sold.seller_name} · Comprador: ${sold.buyer_name}` });
          else if (reserved) toast(`Ficha #${pad(n)} reservada`, { description: `Por ${reserved.seller_name}${reserved.buyer_name ? " · " + reserved.buyer_name : ""}` });
          else if (onPick) onPick(pad(n));
        }}
        className={`aspect-square rounded-[4px] text-[8px] md:text-[10px] font-outfit flex items-center justify-center border transition-colors ${cls}`}
      >
        {pad(n)}
      </button>
    );
  }

  return (
    <section className="rounded-2xl border border-[#F0B91B]/20 bg-[#0A2818]/60 overflow-hidden" data-testid="ficha-map">
      <div className="px-6 py-4 border-b border-[#F0B91B]/15 flex items-center justify-between flex-wrap gap-3">
        <h2 className="font-bebas text-2xl text-[#FDFBF7] tracking-wider flex items-center gap-2">
          <Grid3x3 className="w-5 h-5 text-[#F0B91B]" /> Mapa de fichas
        </h2>
        <button onClick={load} className="inline-flex items-center gap-2 text-xs text-[#FDFBF7]/60 hover:text-[#F0B91B] font-outfit transition-colors" data-testid="ficha-refresh">
          <RefreshCw className="w-3.5 h-3.5" /> Actualizar
        </button>
      </div>

      <div className="p-6 space-y-5">
        <div className="flex flex-wrap items-center gap-4 justify-between">
          <div className="flex flex-wrap gap-4 text-sm font-outfit">
            <span className="text-[#FDFBF7]/80">Total: <strong className="font-bebas text-lg text-[#FDFBF7]">{totalCount.toLocaleString("es-CO")}</strong></span>
            <span className="text-[#E63946]">Vendidas: <strong className="font-bebas text-lg">{soldCount.toLocaleString("es-CO")}</strong></span>
            <span className="text-[#F0B91B]">Reservadas: <strong className="font-bebas text-lg">{reservedCount.toLocaleString("es-CO")}</strong></span>
            <span className="text-[#7FD1AE]">Disponibles: <strong className="font-bebas text-lg">{available.toLocaleString("es-CO")}</strong></span>
          </div>
          <div className="flex items-center gap-3 text-xs font-outfit flex-wrap">
            <span className="inline-flex items-center gap-1.5 text-[#FDFBF7]/70"><span className="w-3 h-3 rounded-[3px] bg-[#7FD1AE]/25 border border-[#7FD1AE]/40" /> Disponible</span>
            <span className="inline-flex items-center gap-1.5 text-[#FDFBF7]/70"><span className="w-3 h-3 rounded-[3px] bg-[#F0B91B]/25 border border-[#F0B91B]/50" /> Reservada</span>
            <span className="inline-flex items-center gap-1.5 text-[#FDFBF7]/70"><span className="w-3 h-3 rounded-[3px] bg-[#E63946]/25 border border-[#E63946]/40" /> Vendida</span>
          </div>
        </div>

        <form onSubmit={checkNumber} className="flex gap-2" data-testid="ficha-search-form">
          <div className="relative flex-1 max-w-xs">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#FDFBF7]/40" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value.replace(/[^0-9]/g, ""))}
              placeholder="Buscar número (0000–9999)"
              className="w-full bg-[#051A0F] border border-[#F0B91B]/20 rounded-xl pl-10 pr-4 py-2.5 text-[#FDFBF7] font-outfit text-sm placeholder:text-[#FDFBF7]/30 focus:outline-none focus:border-[#F0B91B]"
              data-testid="ficha-search-input"
              inputMode="numeric"
              maxLength={4}
            />
          </div>
          <button type="submit" className="px-5 py-2.5 rounded-xl bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] font-bebas tracking-wider text-sm transition-colors" data-testid="ficha-search-btn">
            Consultar
          </button>
        </form>

        {result && (
          <div className="rounded-xl border px-4 py-3 font-outfit text-sm flex flex-wrap items-center gap-3 justify-between"
            style={result.error ? { borderColor: "rgba(240,185,27,0.4)" } : result.state === "sold" ? { borderColor: "rgba(230,57,70,0.4)" } : result.state === "reserved" ? { borderColor: "rgba(240,185,27,0.5)" } : { borderColor: "rgba(127,209,174,0.4)" }}
            data-testid="ficha-search-result">
            <div className="flex items-center gap-3 min-w-0">
              {result.error ? (
                <span className="text-[#F0B91B]">{result.error}</span>
              ) : result.state === "sold" ? (
                <><XCircle className="w-5 h-5 text-[#E63946] shrink-0" /><span className="text-[#FDFBF7]">Ficha <strong>#{pad(result.number)}</strong> — <span className="text-[#E63946]">VENDIDA</span> por {result.seller_name} a {result.buyer_name}</span></>
              ) : result.state === "reserved" ? (
                <><Clock className="w-5 h-5 text-[#F0B91B] shrink-0" /><span className="text-[#FDFBF7]">Ficha <strong>#{pad(result.number)}</strong> — <span className="text-[#F0B91B]">RESERVADA</span> por {result.seller_name}</span></>
              ) : (
                <><CheckCircle2 className="w-5 h-5 text-[#7FD1AE] shrink-0" /><span className="text-[#FDFBF7]">Ficha <strong>#{pad(result.number)}</strong> — <span className="text-[#7FD1AE]">DISPONIBLE</span></span></>
              )}
            </div>
            {!result.error && result.state === "free" && (onCellClick || onPick) && (
              <button
                type="button"
                onClick={() => {
                  const ficha = pad(result.number);
                  if (onCellClick) {
                    onCellClick({ number: result.number, ficha, state: "free", info: null });
                  } else if (onPick) {
                    onPick(ficha);
                  }
                }}
                className="inline-flex items-center gap-2 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] px-4 py-2 rounded-lg font-bebas text-sm tracking-wider shadow-[0_8px_20px_-8px_rgba(240,185,27,0.7)] transition-all hover:-translate-y-0.5"
                data-testid="ficha-search-reserve-btn"
              >
                <Ticket className="w-4 h-4" />
                Registrar esta ficha
              </button>
            )}
          </div>
        )}

        {loading ? (
          <div className="p-10 flex justify-center"><Loader2 className="w-6 h-6 text-[#F0B91B] animate-spin" /></div>
        ) : (
          <>
            <div className="grid gap-1" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(44px, 1fr))" }} data-testid="ficha-grid">
              {cells}
            </div>
            <div className="flex items-center justify-between pt-2">
              <button disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))} className="inline-flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#F0B91B] disabled:opacity-30 font-outfit" data-testid="ficha-prev">
                <ChevronLeft className="w-4 h-4" /> Anterior
              </button>
              <span className="text-sm font-outfit text-[#FDFBF7]/70">
                Fichas <strong className="text-[#F0B91B]">{pad(start)}</strong>–<strong className="text-[#F0B91B]">{pad(end)}</strong>
                <span className="text-[#FDFBF7]/40"> (pág. {page + 1}/{totalPages})</span>
              </span>
              <button disabled={page >= totalPages - 1} onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} className="inline-flex items-center gap-1 text-sm text-[#FDFBF7]/70 hover:text-[#F0B91B] disabled:opacity-30 font-outfit" data-testid="ficha-next">
                Siguiente <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}
      </div>
    </section>
  );
}
